import { PantryCategory } from "../types/pantry";

export interface IngredientData {
  name: string;
  displayName?: string; // Human-friendly label from the backend; falls back to `name`
  aliases?: string[]; // Alternative names (e.g., "scallions" for "green onions")
  category: PantryCategory;
  commonUnit: string; // Most commonly used unit for this ingredient
  alternateUnits?: string[]; // Other valid units for this ingredient
  productSearchTerms: string[]; // Terms for grocery shopping
  isStaple?: boolean; // Whether this is typically a staple item (unlimited quantity)
}

// The ingredient catalog is loaded at runtime from the backend
// (GET /api/ingredients) and cached here in-process. It starts empty and is
// populated once via setIngredientCatalog() during app startup (see
// IngredientCatalogLoader in App.tsx); the helpers below read from it. The
// array is mutated in place so any held reference stays valid after load.
export const INGREDIENT_DATABASE: IngredientData[] = [];

/**
 * Replace the cached ingredient catalog. Mutates the existing array in place
 * (rather than reassigning) so the helpers — and anything else holding the
 * reference — see the new data without re-importing.
 */
export const setIngredientCatalog = (ingredients: IngredientData[]): void => {
  INGREDIENT_DATABASE.length = 0;
  INGREDIENT_DATABASE.push(...ingredients);
};

/** True once the catalog has been loaded from the backend at least once. */
export const isIngredientCatalogLoaded = (): boolean =>
  INGREDIENT_DATABASE.length > 0;

export const searchIngredients = (
  query: string,
  limit: number = 10
): IngredientData[] => {
  if (!query.trim()) return [];

  const searchTerm = query.toLowerCase().trim();
  const results: Array<{ ingredient: IngredientData; score: number }> = [];

  INGREDIENT_DATABASE.forEach((ingredient) => {
    let score = 0;

    // Exact name match gets highest score
    if (ingredient.name.toLowerCase() === searchTerm) {
      score = 100;
    }
    // Name starts with search term
    else if (ingredient.name.toLowerCase().startsWith(searchTerm)) {
      score = 90;
    }
    // Name contains search term
    else if (ingredient.name.toLowerCase().includes(searchTerm)) {
      score = 80;
    }
    // Alias exact match
    else if (
      ingredient.aliases?.some((alias) => alias.toLowerCase() === searchTerm)
    ) {
      score = 95;
    }
    // Alias starts with search term
    else if (
      ingredient.aliases?.some((alias) =>
        alias.toLowerCase().startsWith(searchTerm)
      )
    ) {
      score = 85;
    }
    // Alias contains search term
    else if (
      ingredient.aliases?.some((alias) =>
        alias.toLowerCase().includes(searchTerm)
      )
    ) {
      score = 75;
    }
    // Product search terms match
    else if (
      ingredient.productSearchTerms.some((term) =>
        term.toLowerCase().includes(searchTerm)
      )
    ) {
      score = 60;
    }

    if (score > 0) {
      results.push({ ingredient, score });
    }
  });

  // Sort by score (highest first) and return top results
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((result) => result.ingredient);
};

// Get ingredient by exact name match (including aliases)
export const getIngredientByName = (
  name: string
): IngredientData | undefined => {
  const normalizedName = name.toLowerCase().trim();

  return INGREDIENT_DATABASE.find(
    (ingredient) =>
      ingredient.name.toLowerCase() === normalizedName ||
      ingredient.aliases?.some(
        (alias) => alias.toLowerCase() === normalizedName
      )
  );
};

// Get all ingredients by category
export const getIngredientsByCategory = (
  category: PantryCategory
): IngredientData[] => {
  return INGREDIENT_DATABASE.filter(
    (ingredient) => ingredient.category === category
  );
};

// Get suggested unit for an ingredient (enhances existing unit suggestion logic)
export const getSuggestedUnit = (ingredientName: string): string => {
  const ingredient = getIngredientByName(ingredientName);
  return ingredient?.commonUnit || "each"; // Fallback to existing logic if not found
};

// Get suggested category for an ingredient (enhances existing category suggestion logic)
export const getSuggestedCategory = (
  ingredientName: string
): PantryCategory => {
  const ingredient = getIngredientByName(ingredientName);
  return ingredient?.category || "other"; // Fallback to existing logic if not found
};

// Check if ingredient is typically a staple
export const isTypicallyStaple = (ingredientName: string): boolean => {
  const ingredient = getIngredientByName(ingredientName);
  return ingredient?.isStaple || false;
};
